import type { NodePublic } from '../api/workspace';
import type { PreviewDoc, PreviewKind } from './preview-doc';

/**
 * 워크스페이스 파일 → 미리보기 descriptor (2026-08-12).
 *
 * 채팅 미리보기가 쓰는 `PreviewDoc` 계약을 그대로 만들어 준다. 그래야 기존 경량 렌더러
 * (`ChatArea/preview/{Docx,Xlsx,Pptx,Pdf}Preview.tsx`)를 **한 줄도 고치지 않고** 워크스페이스
 * 패널에서 재사용할 수 있다. 형식 판별은 `preview-doc.ts` 와 같은 기준을 쓴다 —
 * 두 화면이 같은 파일을 다르게 판정하면 "채팅에선 열리는데 워크스페이스에선 안 열린다" 가 된다.
 */

const MIME_KIND: Record<string, PreviewKind> = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'text/markdown': 'markdown',
  'text/x-markdown': 'markdown',
  'text/html': 'html',
  // 한컴 한글 (2026-08-14) — 업로드 허용 목록(files.routes.ts ALLOWED_DOCUMENT_MIMES)과 같은 집합.
  'application/x-hwp': 'hwp',
  'application/haansofthwp': 'hwp',
  'application/vnd.hancom.hwp': 'hwp',
  'application/hwp+zip': 'hwp',
  'application/vnd.hancom.hwpx': 'hwp',
};

const EXT_KIND: Array<[RegExp, PreviewKind]> = [
  [/\.pdf$/i, 'pdf'],
  [/\.docx$/i, 'docx'],
  [/\.xlsx$/i, 'xlsx'],
  [/\.pptx$/i, 'pptx'],
  [/\.(md|markdown)$/i, 'markdown'],
  [/\.html?$/i, 'html'],
  // ⚠ 확장자 폴백이 특히 중요하다 — 이전에 올라간 .hwp 파일은 브라우저가 빈 MIME 을 줘
  //   application/octet-stream 으로 저장돼 있다(업로드 declare 는 2026-08-14 부터).
  [/\.hwpx?$/i, 'hwp'],
];

/** 이 파일을 미리보기할 수 있으면 kind, 아니면 null. MIME 우선 → 확장자 폴백. */
export function previewKindOfNode(node: Pick<NodePublic, 'type' | 'name' | 'mimeType'>): PreviewKind | null {
  if (node.type === 'folder') return null;
  const mime = (node.mimeType ?? '').toLowerCase().split(';')[0]!.trim();
  if (MIME_KIND[mime]) return MIME_KIND[mime]!;
  for (const [re, kind] of EXT_KIND) {
    if (re.test(node.name)) return kind;
  }
  return null;
}

/** 경량 렌더러에 그대로 넘길 descriptor. */
export function previewDocOfNode(node: NodePublic): PreviewDoc | null {
  const kind = previewKindOfNode(node);
  if (!kind) return null;
  return {
    key: `workspace:${node.id}`,
    title: node.name,
    kind,
    source: { kind: 'attachment', fileId: node.id },
  };
}

/**
 * 인앱 편집기로 열 수 있는 형식인가.
 *
 * PR6=markdown · PR7=docx(Word) · PR8=pdf · PR9=xlsx(Excel). PPT 는 별도 PR 로 붙으며,
 * 그때 이 함수에 한 줄 추가된다. **여기가 유일한 판정 지점**이어야 "편집 버튼이 보이는데
 * 눌러도 안 열리는" 불일치가 안 생긴다.
 *
 * pdf 는 보기·검색·인쇄(PR8a) → 저장·페이지 조작(PR8b) → 텍스트 편집(PR8c) →
 * 이미지 편집(PR8d) 순으로 열렸다. 상류에서 Electron main 이 하던 PDFium·hb-subset wasm
 * 단계를 브라우저로 재호스팅한 결과다(packages/office/src/pdf-wasm-bridge.ts).
 *
 * pptx 는 열람(PR10b)에서 시작해 **편집·저장까지 열렸다**(PR10c-1~13: 글·도형·슬라이드·표·
 *   그림·차트·애니메이션·마스터·인쇄). 상류가 Electron 메인에서 만들던 렌더 트리를 브라우저에서
 *   만든다(packages/office/src/slides-session.ts).
 *   ⚠ 아직 못 하는 것은 `slides-api-shim.ts` 의 `DOCUMENT_OPS` 가 이름으로 적어 두고 각각
 *     **그 기능만** 안 된다고 답한다(발표자 보기·이미지 검색 등). 2026-09-02 이전에는 그 자리에서
 *     "PowerPoint 편집은 아직 준비 중" 이라고 답해, 되는 편집까지 안 되는 것처럼 보였다.
 *
 * ⚠ hwp 는 현재 **편집 전용**이다(PR4). 채팅과 공유하는 경량 렌더러가 없어 미리보기 모드에는
 *   "편집기로 열기" 안내만 띄운다 — 종전의 "지원하지 않는 형식" 보다는 낫고, 없는 렌더러를
 *   있는 척하지도 않는다. 경량 미리보기는 후속 PR.
 *
 * wasm 은 **문서를 실제로 열 때만** 내려온다 — 목록만 볼 때는 한 바이트도 받지 않는다.
 */
/**
 * 워크스페이스 패널이 **열 수 있는** 것 — 미리보기 kind 에 텍스트·이미지를 더한 집합
 * (2026-08-14 additive).
 *
 * `previewKindOfNode` 는 채팅과 공유하는 계약이라 손대지 않는다. 그쪽은 "경량 렌더러가
 * 있는 형식" 을 뜻하고, 여기서는 "패널에서 뭐라도 보여 줄 수 있는 형식" 을 뜻한다 —
 * 이미지는 `<img>` 하나면 되고 텍스트는 원문을 그대로 뿌리면 되므로 렌더러가 필요 없다.
 */
export type WorkspaceOpenKind = PreviewKind | 'text' | 'image' | 'video' | 'audio';

/**
 * 확장자로 텍스트라고 볼 것들.
 *
 * MIME 만 믿을 수 없다 — 업로드 경로가 브라우저가 준 MIME 을 그대로 declare 하는데,
 * `.log`·`.csv`·`.json` 은 브라우저마다 빈 문자열이나 `application/octet-stream` 이 온다.
 */
const TEXT_EXT = /\.(txt|log|csv|tsv|json|ya?ml|xml|ini|conf|env|sql|sh|bat|ps1|py|rb|go|rs|java|kt|swift|c|h|cpp|cs|php|ts|tsx|js|jsx|css|scss)$/i;

/**
 * 확장자로 미디어라고 볼 것들.
 *
 * ⚠ MIME 만으로 판정하면 **운영에서 조용히 안 열린다**. 워크스페이스 파일 대부분이 채팅·메일
 *   업로드를 타고 들어온 것이라 브라우저가 준 MIME 을 그대로 declare 하는데, 그 값이 비어 있거나
 *   `application/octet-stream` 인 경우가 있다(특히 모바일 업로드·드래그앤드롭). 확장자 폴백이
 *   없으면 그 파일들은 "미리보기를 지원하지 않는 형식" 으로 떨어진다.
 */
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|avif|heic|heif|svg|ico)$/i;
const VIDEO_EXT = /\.(mp4|m4v|webm|ogv|mov|mkv|avi)$/i;
const AUDIO_EXT = /\.(mp3|m4a|aac|wav|ogg|oga|flac|opus)$/i;

/** 텍스트로 다룰 MIME 인가 — `text/*` 와 몇몇 구조화 텍스트. */
function isTextMime(mime: string): boolean {
  return mime.startsWith('text/')
    || mime === 'application/json'
    || mime === 'application/xml'
    || mime === 'application/x-yaml';
}

/**
 * 이 파일을 패널에서 어떻게 열 것인가. 못 열면 null.
 *
 * ⚠ 순서가 계약이다 — 경량 렌더러가 있는 형식(md/docx/…)이 **먼저**다. `.md` 는 text/* 이기도
 *   해서 텍스트 판정을 앞에 두면 마크다운 편집기로 가는 길이 막힌다.
 */
export function workspaceOpenKindOfNode(
  node: Pick<NodePublic, 'type' | 'name' | 'mimeType'>,
): WorkspaceOpenKind | null {
  const kind = previewKindOfNode(node);
  if (kind) return kind;
  if (node.type === 'folder') return null;
  const mime = (node.mimeType ?? '').toLowerCase().split(';')[0]!.trim();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (isTextMime(mime)) return 'text';
  // MIME 이 비었거나 octet-stream 인 파일 — 확장자로 한 번 더 본다(위 주석 참조).
  if (IMAGE_EXT.test(node.name)) return 'image';
  if (VIDEO_EXT.test(node.name)) return 'video';
  if (AUDIO_EXT.test(node.name)) return 'audio';
  return TEXT_EXT.test(node.name) ? 'text' : null;
}

export function isEditableKind(
  kind: PreviewKind | null,
): kind is 'markdown' | 'docx' | 'pdf' | 'xlsx' | 'pptx' | 'hwp' {
  return (
    kind === 'markdown' || kind === 'docx' || kind === 'pdf' || kind === 'xlsx'
    || kind === 'pptx' || kind === 'hwp'
  );
}
